import { useEffect, useMemo, useRef, useState } from "react";
import { apiFetch, apiGet } from "@/lib/api";
import { toast } from "@/components/ui/feedback";

type RentalSourceExportPeriod = "day" | "week" | "month" | "quarter" | "year";
type RentalSourceExportStatus = "queued" | "running" | "completed" | "partial" | "failed" | "expired";

interface RentalSourceExportGroup {
  shipGroupId: string;
  name: string;
  routeId: string;
  routeName: string;
  routeDirection: string;
}

interface RentalSourceExportRequest {
  shipGroupId: string;
  routeId: string;
  routeDirection: string;
  period: RentalSourceExportPeriod;
  includeTestBox: boolean;
}

interface RentalSourceExportScope extends RentalSourceExportRequest {
  shipGroupName: string;
  routeName: string;
  since: string;
  until: string;
}

interface RentalSourceExportPreview {
  scope: RentalSourceExportScope;
  usageCount: number;
  sourceCount: number;
  missingUsageCount: number;
}

interface RentalSourceExportJob {
  id: string;
  scope: RentalSourceExportScope;
  status: RentalSourceExportStatus;
  usageCount: number;
  total: number;
  processed: number;
  succeeded: number;
  failed: number;
  missingUsageCount: number;
  error: string;
  filename: string;
  createdAt: string;
  expiresAt: string;
  downloadAvailable: boolean;
}

interface RentalSourceExportListResponse {
  exports: RentalSourceExportJob[];
}

interface RentalSourceExportPanelProps {
  group: RentalSourceExportGroup;
  period: RentalSourceExportPeriod;
  includeTestBox: boolean;
}

const PERIOD_LABELS: Record<RentalSourceExportPeriod, string> = {
  day: "今日",
  week: "本周",
  month: "本月",
  quarter: "本季",
  year: "本年",
};

const STATUS_META: Record<RentalSourceExportStatus, { label: string; className: string }> = {
  queued: { label: "排队中", className: "bg-gray-100 text-gray-700" },
  running: { label: "处理中", className: "bg-sky-50 text-sky-700" },
  completed: { label: "全部成功", className: "bg-emerald-50 text-emerald-700" },
  partial: { label: "部分失败", className: "bg-amber-50 text-amber-800" },
  failed: { label: "任务失败", className: "bg-red-50 text-red-700" },
  expired: { label: "已过期", className: "bg-gray-100 text-gray-500" },
};

function directionLabel(direction: string): string {
  if (direction === "up") return "上行";
  if (direction === "down") return "下行";
  return "未指定方向";
}

function formatShanghaiDateTime(value: string): string {
  if (!value) return "—";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return value;
  return new Intl.DateTimeFormat("zh-CN", {
    timeZone: "Asia/Shanghai",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date).replace(/\//g, "-");
}


function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

async function responseError(res: Response, fallback: string): Promise<Error> {
  const text = await res.text().catch(() => "");
  if (!text) return new Error(`${fallback}（${res.status}）`);
  try {
    const body = JSON.parse(text) as { error?: string; message?: string };
    return new Error(body.error || body.message || text);
  } catch {
    return new Error(text);
  }
}

async function postJson<T>(path: string, body: unknown, signal: AbortSignal): Promise<T> {
  const res = await apiFetch(path, {
    method: "POST",
    signal,
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await responseError(res, "请求失败");
  return (await res.json()) as T;
}

function scopeKey(scope: Pick<RentalSourceExportScope, "shipGroupId" | "routeId" | "routeDirection">): string {
  return `${scope.shipGroupId}|${scope.routeId}|${scope.routeDirection || ""}`;
}


function downloadFilename(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const utf8 = header.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (utf8) {
    try {
      return decodeURIComponent(utf8);
    } catch {
      // Use the ordinary filename parameter below.
    }
  }
  return header.match(/filename="?([^";]+)"?/i)?.[1]?.trim() || fallback;
}

export default function RentalSourceExportPanel({ group, period, includeTestBox }: RentalSourceExportPanelProps) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState<RentalSourceExportPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [previewRetry, setPreviewRetry] = useState(0);
  const [jobs, setJobs] = useState<RentalSourceExportJob[]>([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsError, setJobsError] = useState("");
  const [jobsRetry, setJobsRetry] = useState(0);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState("");
  const [downloadingId, setDownloadingId] = useState("");
  const [now, setNow] = useState(() => Date.now());
  const previewTokenRef = useRef(0);
  const scopeKeyRef = useRef("");
  const mountedRef = useRef(true);
  const downloadAbortRef = useRef<AbortController | null>(null);
  const startAbortRef = useRef<AbortController | null>(null);
  const currentScopeKey = `${group.shipGroupId}|${group.routeId}|${group.routeDirection || ""}`;
  scopeKeyRef.current = currentScopeKey;

  const request = useMemo<RentalSourceExportRequest>(() => ({
    shipGroupId: group.shipGroupId,
    routeId: group.routeId,
    routeDirection: group.routeDirection || "",
    period,
    includeTestBox: !!includeTestBox && period === "day",
  }), [group.shipGroupId, group.routeDirection, group.routeId, includeTestBox, period]);
  const exportAvailable = period === "day";

  useEffect(() => {
    if (exportAvailable) return;
    setOpen(false);
    previewTokenRef.current += 1;
    downloadAbortRef.current?.abort();
    downloadAbortRef.current = null;
    startAbortRef.current?.abort();
    startAbortRef.current = null;
    setPreview(null);
    setPreviewError("");
    setStartError("");
    setPreviewLoading(false);
    setStarting(false);
  }, [exportAvailable]);

  useEffect(() => {
    if (!open || !exportAvailable) return;
    const controller = new AbortController();
    const token = ++previewTokenRef.current;
    setPreview(null);
    setPreviewError("");
    setStartError("");
    setPreviewLoading(true);
    postJson<RentalSourceExportPreview>("/rental/stats/source-exports/preview", request, controller.signal)
      .then((result) => {
        if (controller.signal.aborted || token !== previewTokenRef.current || scopeKeyRef.current !== currentScopeKey) return;
        setPreview(result);
      })
      .catch((error) => {
        if (isAbortError(error) || controller.signal.aborted || token !== previewTokenRef.current || scopeKeyRef.current !== currentScopeKey) return;
        setPreviewError(error instanceof Error ? error.message : "预览失败，请重试");
      })
      .finally(() => {
        if (!controller.signal.aborted && token === previewTokenRef.current && scopeKeyRef.current === currentScopeKey) {
          setPreviewLoading(false);
        }
      });
    return () => controller.abort();
  }, [currentScopeKey, exportAvailable, open, previewRetry, request]);
  useEffect(() => {
    if (!open || !exportAvailable) return;
    const controller = new AbortController();
    let inFlight = false;
    let firstLoad = true;
    setJobs([]);
    setJobsError("");

    const loadJobs = async () => {
      if (inFlight || controller.signal.aborted) return;
      inFlight = true;
      if (firstLoad) setJobsLoading(true);
      const query = new URLSearchParams({
        shipGroupId: group.shipGroupId,
        routeId: group.routeId,
        routeDirection: group.routeDirection || "",
      });
      try {
        const response = await apiGet<RentalSourceExportListResponse>(`/rental/stats/source-exports?${query.toString()}`, controller.signal);
        if (controller.signal.aborted || scopeKeyRef.current !== currentScopeKey) return;
        const incoming = (response.exports || []).filter((job) => scopeKey(job.scope) === currentScopeKey);
        setJobs((previous) => {
          const merged = new Map<string, RentalSourceExportJob>();
          for (const job of previous) {
            if (scopeKey(job.scope) === currentScopeKey) merged.set(job.id, job);
          }
          for (const job of incoming) merged.set(job.id, job);
          return Array.from(merged.values()).sort((a, b) => {
            const aTime = new Date(a.createdAt).getTime();
            const bTime = new Date(b.createdAt).getTime();
            return (Number.isFinite(bTime) ? bTime : 0) - (Number.isFinite(aTime) ? aTime : 0);
          });
        });
        setJobsError("");
      } catch (error) {
        if (!isAbortError(error) && !controller.signal.aborted && scopeKeyRef.current === currentScopeKey) {
          setJobsError("最近导出任务加载失败，请重试");
        }
      } finally {
        inFlight = false;
        if (firstLoad) {
          firstLoad = false;
          if (!controller.signal.aborted) setJobsLoading(false);
        }
      }
    };

    void loadJobs();
    const timer = window.setInterval(() => { void loadJobs(); }, 5000);
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [currentScopeKey, exportAvailable, group.shipGroupId, group.routeDirection, group.routeId, jobsRetry, open, period]);

  useEffect(() => {
    if (!open) return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, [open]);

  useEffect(() => {
    if (open) return;
    downloadAbortRef.current?.abort();
    downloadAbortRef.current = null;
    startAbortRef.current?.abort();
    startAbortRef.current = null;
    if (mountedRef.current) {
      setDownloadingId("");
      setStarting(false);
    }
  }, [open]);

  useEffect(() => () => {
    downloadAbortRef.current?.abort();
    downloadAbortRef.current = null;
    startAbortRef.current?.abort();
    startAbortRef.current = null;
    if (mountedRef.current) setDownloadingId("");
  }, [currentScopeKey]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const previewScope = preview?.scope;
  const activeJob = jobs.some((job) => job.status === "queued" || job.status === "running");
  const refreshPreview = () => setPreviewRetry((value) => value + 1);
  const refreshJobs = () => setJobsRetry((value) => value + 1);

  const startExport = async () => {
    if (!exportAvailable || !mountedRef.current || !preview || preview.sourceCount === 0 || starting || jobsLoading || activeJob) return;
    const capturedScopeKey = currentScopeKey;
    const controller = new AbortController();
    startAbortRef.current = controller;
    setStarting(true);
    setStartError("");
    try {
      const job = await postJson<RentalSourceExportJob>("/rental/stats/source-exports", request, controller.signal);
      if (!mountedRef.current || scopeKeyRef.current !== capturedScopeKey) return;
      setJobs((previous) => [job, ...previous.filter((item) => item.id !== job.id)]);
      toast("源图包已创建，后台处理中", "success");
    } catch (error) {
      if (!isAbortError(error) && mountedRef.current && scopeKeyRef.current === capturedScopeKey) {
        const message = error instanceof Error ? error.message : "请重试";
        setStartError(message);
        toast(`创建导出任务失败：${message}`, "error");
      }
    } finally {
      if (startAbortRef.current === controller) startAbortRef.current = null;
      if (mountedRef.current && scopeKeyRef.current === capturedScopeKey) setStarting(false);
    }
  };

  const downloadJob = async (job: RentalSourceExportJob) => {
    if (!exportAvailable || !job.downloadAvailable || downloadingId || scopeKeyRef.current !== currentScopeKey) return;
    const capturedScopeKey = currentScopeKey;
    downloadAbortRef.current?.abort();
    const controller = new AbortController();
    downloadAbortRef.current = controller;
    setDownloadingId(job.id);
    try {
      const res = await apiFetch(`/rental/stats/source-exports/${encodeURIComponent(job.id)}/download`, {
        cache: "no-store",
        signal: controller.signal,
      });
      if (!res.ok) throw await responseError(res, "下载失败");
      const blob = await res.blob();
      if (controller.signal.aborted || scopeKeyRef.current !== capturedScopeKey) return;
      const filename = downloadFilename(res.headers.get("Content-Disposition"), job.filename || `rental-source-export-${job.id}.zip`);
      const url = URL.createObjectURL(blob);
      try {
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
      } finally {
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
      toast("源图包下载已开始", "success");
    } catch (error) {
      if (!isAbortError(error) && scopeKeyRef.current === capturedScopeKey) {
        toast(`下载源图包失败：${error instanceof Error ? error.message : "请重试"}`, "error");
      }
    } finally {
      if (downloadAbortRef.current === controller) {
        downloadAbortRef.current = null;
        setDownloadingId("");
      }
    }
  };

  const displayScope = previewScope || {
    shipGroupName: group.name,
    routeName: group.routeName || group.routeId || "未绑定航线",
    routeDirection: group.routeDirection || "",
    since: "",
    until: "",
  };

  return (
    <>
      <button
        type="button"
        onClick={() => {
          if (exportAvailable) setOpen(true);
        }}
        disabled={!exportAvailable}
        title={exportAvailable ? undefined : "仅今日可导出源图"}
        aria-haspopup="dialog"
        aria-expanded={exportAvailable && open}
        className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 text-sm font-medium text-teal-700 hover:bg-teal-100 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
      >
        <i className="ri-archive-drawer-line" aria-hidden="true" />
        导出源图包
      </button>
      {!exportAvailable && <span className="text-xs text-gray-400">仅今日可导出源图</span>}

      {open && exportAvailable && (
        <div
          className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40 p-2 sm:items-center sm:p-4"
          onClick={() => setOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="rental-source-export-title"
            className="flex max-h-[calc(100dvh-env(safe-area-inset-top)-12px)] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl sm:max-h-[90vh]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 border-b border-gray-100 p-4 sm:p-5">
              <div className="min-w-0">
                <h2 id="rental-source-export-title" className="text-base font-bold text-gray-900">
                  <i className="ri-archive-drawer-line mr-1.5 text-teal-600" aria-hidden="true" />
                  导出源图包
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="关闭导出源图包面板"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <i className="ri-close-line text-xl" aria-hidden="true" />
              </button>
            </div>

            <div className="flex-1 space-y-4 overflow-y-auto p-4 sm:p-5">
              <section aria-labelledby="rental-source-export-scope" className="space-y-2">
                <h3 id="rental-source-export-scope" className="text-sm font-semibold text-gray-900">导出范围</h3>
                <div className="grid grid-cols-1 gap-2 text-xs text-gray-600 sm:grid-cols-2">
                  <div className="rounded-lg bg-gray-50 px-3 py-2">
                    <div className="text-gray-400">船组</div>
                    <div className="mt-0.5 font-medium text-gray-800">{displayScope.shipGroupName || group.name}</div>
                  </div>
                  <div className="rounded-lg bg-gray-50 px-3 py-2">
                    <div className="text-gray-400">航线 / 方向</div>
                    <div className="mt-0.5 font-medium text-gray-800">{displayScope.routeName || "未绑定航线"} · {directionLabel(displayScope.routeDirection)}</div>
                  </div>
                  <div className="rounded-lg bg-gray-50 px-3 py-2 sm:col-span-2">
                    <div className="font-medium text-gray-800">{PERIOD_LABELS[period]}</div>
                    <div className="mt-1 text-gray-500">
                      {displayScope.since && displayScope.until
                        ? `${formatShanghaiDateTime(displayScope.since)} 至 ${formatShanghaiDateTime(displayScope.until)}（北京时间）`
                        : "正在获取起止时间…"}
                    </div>
                  </div>
                </div>
              </section>


              <section className="rounded-xl border border-teal-100 bg-teal-50/60 p-3 sm:p-4" aria-labelledby="rental-source-export-preview">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 id="rental-source-export-preview" className="text-sm font-semibold text-gray-900">预览数量</h3>
                  {previewLoading && <span className="text-xs text-teal-700">正在计算…</span>}
                  <button
                    type="button"
                    onClick={refreshPreview}
                    disabled={previewLoading}
                    className="ml-auto inline-flex min-h-[36px] items-center gap-1 rounded-md border border-teal-200 bg-white px-2.5 py-1.5 text-xs font-medium text-teal-700 hover:bg-teal-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <i className="ri-refresh-line" aria-hidden="true" />重试预览
                  </button>
                </div>
                {previewError ? (
                  <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
                    {previewError}
                  </div>
                ) : preview ? (
                  <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                    <div className="rounded-lg bg-white px-2 py-2.5">
                      <div className="text-xl font-bold tabular-nums text-teal-700">{preview.usageCount}</div>
                      <div className="text-xs text-gray-500">使用记录</div>
                    </div>
                    <div className="rounded-lg bg-white px-2 py-2.5">
                      <div className="text-xl font-bold tabular-nums text-teal-700">{preview.sourceCount}</div>
                      <div className="text-xs text-gray-500">待处理源图</div>
                    </div>
                    <div className="rounded-lg bg-white px-2 py-2.5">
                      <div className="text-xl font-bold tabular-nums text-amber-700">{preview.missingUsageCount}</div>
                      <div className="text-xs text-gray-500">未上传记录</div>
                    </div>
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-gray-500">等待预览…</p>
                )}
              </section>

              {startError && (
                <div className="flex flex-wrap items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
                  <span className="flex-1 break-words">{startError}</span>
                  <button type="button" onClick={refreshPreview} className="font-medium underline">重新预览</button>
                </div>
              )}
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={() => void startExport()}
                  disabled={!exportAvailable || starting || previewLoading || !preview || preview.sourceCount === 0 || activeJob}
                  className="inline-flex min-h-[46px] flex-1 items-center justify-center gap-1.5 rounded-lg bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <i className={starting ? "ri-loader-4-line animate-spin" : "ri-play-circle-line"} aria-hidden="true" />
                  {starting ? "创建中…" : "导出源图包"}
                </button>
                {!preview && !previewLoading && !previewError && <span className="text-xs text-gray-400">等待预览后可创建任务</span>}
                {!exportAvailable && <span className="text-xs text-gray-500">仅今日可导出源图</span>}
                {activeJob && <span className="text-xs text-gray-500">已有进行中的导出任务，完成后可再次导出。</span>}
                {preview?.sourceCount === 0 && <span className="text-xs text-gray-500">当前冻结范围没有可导出的源图，无法创建导出任务。</span>}
              </div>

              <section className="space-y-2" aria-labelledby="rental-source-export-jobs">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 id="rental-source-export-jobs" className="text-sm font-semibold text-gray-900">最近任务</h3>
                  <button
                    type="button"
                    onClick={refreshJobs}
                    disabled={jobsLoading}
                    className="ml-auto inline-flex min-h-[36px] items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <i className="ri-refresh-line" aria-hidden="true" />刷新任务
                  </button>
                </div>
                {jobsError && (
                  <div className="flex flex-wrap items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
                    <span className="flex-1">{jobsError}</span>
                    <button type="button" onClick={refreshJobs} className="font-medium underline">重试</button>
                  </div>
                )}
                {jobsLoading ? (
                  <div className="rounded-lg border border-gray-200 bg-white px-3 py-6 text-center text-sm text-gray-400">加载最近任务…</div>
                ) : jobs.length === 0 && !jobsError ? (
                  <div className="rounded-lg border border-dashed border-gray-200 bg-white px-3 py-6 text-center text-sm text-gray-400">当前范围暂无最近任务</div>
                ) : (
                  <div className="space-y-2">
                    {jobs.map((job) => {
                      const expiredByTime = !!job.expiresAt && Number.isFinite(new Date(job.expiresAt).getTime()) && new Date(job.expiresAt).getTime() <= now;
                      const status: RentalSourceExportStatus = expiredByTime ? "expired" : job.status;
                      const meta = STATUS_META[status];
                      const progress = job.total > 0 ? Math.min(100, Math.max(0, Math.round((job.processed / job.total) * 100))) : 0;
                      const downloadable = !expiredByTime && job.downloadAvailable && (status === "completed" || status === "partial");
                      return (
                        <article key={job.id} className="rounded-xl border border-gray-200 bg-white p-3 sm:p-4">
                          <div className="flex flex-wrap items-start gap-2">
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${meta.className}`}>{meta.label}</span>
                                <span className="text-xs text-gray-500">创建于 {formatShanghaiDateTime(job.createdAt)}</span>
                              </div>
                              <div className="mt-1 text-xs text-gray-400">
                                北京时间：{job.scope.since && job.scope.until ? `${formatShanghaiDateTime(job.scope.since)} 至 ${formatShanghaiDateTime(job.scope.until)}` : "范围未返回"}
                              </div>
                            </div>
                            {downloadable && (
                              <button
                                type="button"
                                onClick={() => void downloadJob(job)}
                                disabled={!!downloadingId}
                                className="inline-flex min-h-[40px] items-center gap-1 rounded-lg bg-teal-600 px-3 py-2 text-xs font-medium text-white hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                <i className={downloadingId === job.id ? "ri-loader-4-line animate-spin" : "ri-download-2-line"} aria-hidden="true" />
                                {downloadingId === job.id ? "下载中…" : "下载 ZIP"}
                              </button>
                            )}
                          </div>
                          {(status === "queued" || status === "running") && (
                            <div className="mt-3 space-y-1.5">
                              <div className="h-2 overflow-hidden rounded-full bg-gray-100" aria-label={`任务进度 ${progress}%`}>
                                <div className="h-full rounded-full bg-teal-500 transition-all" style={{ width: `${progress}%` }} />
                              </div>
                              <div className="flex flex-wrap justify-between gap-2 text-xs text-gray-500">
                                <span>{job.processed}/{job.total} 张源图已处理</span>
                                <span>{progress}%</span>
                              </div>
                            </div>
                          )}
                          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
                            <span>成功 {job.succeeded}</span>
                            {job.failed > 0 && <span className="text-amber-700">失败 {job.failed}</span>}
                            {job.missingUsageCount > 0 && <span className="text-amber-700">未上传 {job.missingUsageCount}</span>}
                          </div>
                          {status === "failed" && job.error && <p className="mt-2 break-words text-xs text-red-700">{job.error}</p>}
                          {status === "expired" ? (
                            <p className="mt-2 text-xs text-gray-500">已过期，请重新导出。</p>
                          ) : (
                            <p className="mt-2 text-xs text-gray-400">下载有效至 {formatShanghaiDateTime(job.expiresAt)}</p>
                          )}
                        </article>
                      );
                    })}
                  </div>
                )}
              </section>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
